import { debounce } from 'lodash';
import type { CSSProperties, FormEvent, KeyboardEvent, MouseEvent, Ref } from 'react';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import clsx from 'clsx';
import useTestTag from '../../common/hooks/use-test-tag';
import { combineRefs } from '../../common/functions/combine-refs';

export type ContentEditableTypeAttribute = 'text' | 'numeric' | 'decimal' | 'tel' | 'search';

export interface ContentEditableInputRef {
  clearText: () => void;
  setInnerText: (innerText: string) => void;
  setInnerHTML: (innerHTML: string) => void;
  getInnerHTML: () => string;
  focus: () => void;
  blur: () => void;
}

export interface ContentEditableInputProps {
  type?: ContentEditableTypeAttribute;
  className?: string;
  placeholder?: string;
  placeholderVisible?: boolean;
  onInput?: (event: FormEvent<HTMLDivElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onTextChange?: (value: string) => void;
  onHTMLChange?: (value: string) => void;
  onClick?: (event: MouseEvent) => void;
  /**
   * This is used to delay the firing of onInput for the number of milliseconds defined.
   */
  debounceTime?: number;
  height?: number;
  width?: number;
  isSingleLine?: boolean;
  allowEdit?: boolean;
}

function ContentEditableInput(props: ContentEditableInputProps, ref: Ref<ContentEditableInputRef>) {
  const {
    type = 'text',
    className,
    placeholder,
    placeholderVisible = true,
    onInput,
    onFocus,
    onBlur,
    onTextChange,
    onHTMLChange,
    onClick,
    debounceTime,
    height,
    width,
    isSingleLine = false,
    allowEdit = true,
  } = props;

  const tagCreator = useTestTag('content-editable');

  const editableDiv = useRef<HTMLElement>();
  const focusListener = useRef((event: FocusEvent) => {
    const value = (event.target as HTMLElement).innerHTML;
    placeholder && value === placeholder && ((event.target as HTMLElement).innerHTML = '');

    onFocus?.();
  });
  const blurListener = useRef((event: FocusEvent) => {
    const value = (event.target as HTMLElement).innerHTML;
    placeholder && placeholderVisible && value === '' && ((event.target as HTMLElement).innerHTML = placeholder);

    onBlur?.();
  });

  const onElementCreated = (element: HTMLElement) => {
    if (editableDiv.current) {
      editableDiv.current.removeEventListener('focus', focusListener.current);
      editableDiv.current.removeEventListener('blur', blurListener.current);
    }

    editableDiv.current = element;
    if (allowEdit && placeholder && editableDiv.current && editableDiv.current !== document.activeElement) {
      editableDiv.current.innerHTML === '' && placeholderVisible && (element.innerHTML = placeholder);
      !placeholderVisible && (element.innerHTML = '');
    }

    editableDiv.current?.addEventListener('focus', focusListener.current);
    editableDiv.current?.addEventListener('blur', blurListener.current);
  };

  const onInputChanged = (event: FormEvent<HTMLDivElement>) => {
    onInput?.(event);
    //Workaround for a firefox browser issue where empty space is added after clearing an contenteditable div or span with backspace
    onTextChange?.((event.target as HTMLElement).innerText.trim());
    onHTMLChange?.((event.target as HTMLElement).innerHTML);
  };
  const onInputHandler = debounceTime ? debounce(onInputChanged, debounceTime) : onInputChanged;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  /**
   * Programmatically setting the inner text or html does not fire the onInput event, so this function will do that for
   * us.
   */
  const clearText = () => {
    setInnerText('');
    setInnerHTML('');

    onTextChange?.('');
    onHTMLChange?.('');
  };

  const setInnerText = (innerText: string) => {
    if (editableDiv.current && editableDiv.current.innerText !== innerText) {
      editableDiv.current.innerText = innerText || '';
    }
  };

  const setInnerHTML = (innerHTML: string) => {
    if (editableDiv.current && editableDiv.current.innerHTML !== innerHTML) {
      editableDiv.current.innerHTML = innerHTML;
    }
  };

  // When using a field ref, `react-hook-form` checks if the underlying element has a 'focus' method,
  // so we need to name this method the same to match that expectation, otherwise the internal field ref is not initialized.
  // See: https://github.com/react-hook-form/react-hook-form/blob/b5da7623c3052d25e5e455e78d9bbbd7612b1cea/src/useController.ts#L106
  const focus = () => {
    editableDiv.current?.focus();
  };

  const blur = () => {
    editableDiv.current?.blur();
  };

  const getInnerHTML = () => {
    return editableDiv?.current?.innerHTML ?? '';
  };

  useImperativeHandle(ref, () => ({
    clearText,
    getInnerHTML,
    setInnerText,
    setInnerHTML,
    focus,
    blur,
  }));

  const styles: CSSProperties = {};
  if (height) {
    styles['height'] = `${height}px`;
  }
  if (width) {
    styles['width'] = `${width}px`;
  }

  const dynamicArgs: Record<string, unknown> = {};

  const spanClassName = clsx('[&_a]:odin-text-url [&_a]:odin-underline', className, {
    'odin-overflow-x-auto odin-overflow-y-hidden odin-whitespace-pre': allowEdit && isSingleLine,
    'odin-truncate': !allowEdit && isSingleLine,
  });

  if (isSingleLine) {
    dynamicArgs['onKeyDown'] = onKeyDown;
  }

  if (!allowEdit && onClick) {
    dynamicArgs['onClick'] = onClick;
  }

  const inputRef = (element: HTMLSpanElement) => element && onElementCreated(element);
  const combinedRef = combineRefs(inputRef, tagCreator());

  return (
    <span
      className={spanClassName}
      contentEditable={allowEdit}
      ref={combinedRef}
      onInput={onInputHandler}
      inputMode={type}
      style={{ ...styles, display: 'block' }}
      {...dynamicArgs}
    />
  );
}

export default forwardRef(ContentEditableInput);
